import { useEffect, useMemo, useState } from 'react'

import { useWindow, useQueryResult } from '@krp/client-runtime'
import {
  PriceTagsScreen,
  cameraScanAvailable,
  inventoryReloadRequested,
  makeSelectPriceTagsVm,
  normalizeRid,
  onecStructure,
  priceTagCopiesChanged,
  priceTagRemoved,
  priceTagScanRequested,
  priceTagTemplateChosen,
  priceTagWarehouseChosen,
  priceTagsCleared,
  selectStructureWarehouses,
  type PriceTagsActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'

/** Как часто экран пересчитывает возраст цен в подборе. */
const AGE_TICK_MS = 60_000

/**
 * Binding окна «Ценники 1С»: rid — из окна (D-064), склады — из структуры этой базы.
 * Подбор живёт в слайсе со сроком `device`, ответы службы — в кэше.
 */
export function PriceTagsBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const tags = useAppSelector((state) => state.onec.priceTags)
  const scan = useAppSelector((state) => state.onec.priceTagScan)

  const resourceArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const warehouses = useQueryResult(onecStructure, resourceArgs)

  const [now, setNow] = useState(() => new Date().toISOString())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date().toISOString()), AGE_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  const selectVm = useMemo(() => makeSelectPriceTagsVm(), [])
  const vm = selectVm({ resourceId, tags, scan, warehouses, now, cameraScanAvailable: cameraScanAvailable() })

  const actions = useMemo<PriceTagsActions>(
    () => ({
      warehouseChosen: (refKey) => {
        const found = (warehouses.data === undefined ? [] : selectStructureWarehouses(warehouses.data.data)).find((item) => item.refKey === refKey)
        if (found !== undefined) dispatch(priceTagWarehouseChosen({ resourceId, refKey: found.refKey, name: found.name }))
      },
      reloadWarehouses: () => dispatch(inventoryReloadRequested({ resourceId, warehouseRefKey: null })),
      templateChosen: (templateId) => dispatch(priceTagTemplateChosen({ resourceId, templateId })),
      scanSubmitted: (barcode) => dispatch(priceTagScanRequested({ resourceId, barcode })),
      copiesChanged: (productRefKey, copies) => dispatch(priceTagCopiesChanged({ resourceId, productRefKey, copies })),
      itemRemoved: (productRefKey) => dispatch(priceTagRemoved({ resourceId, productRefKey })),
      cleared: () => dispatch(priceTagsCleared({ resourceId })),
    }),
    [dispatch, resourceId, warehouses.data],
  )

  return <PriceTagsScreen vm={vm} actions={actions} />
}
